/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Default Error Fallback Component
 *
 * Default error fallback UI extracted from ErrorBoundary for modularity.
 */

"use client";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { logger } from "@/lib/utils/logger";
import { AlertCircle, Bug, Home, RefreshCw } from "lucide-react";
import { useState } from "react";

export interface ErrorFallbackProps {
  error: Error;
  errorInfo: {
    componentStack: string;
    errorBoundary?: string;
    errorMessage?: string;
  } | null;
  resetError: () => void;
  eventId?: string;
  showDetails?: boolean;
}

/**
 * Default Error Fallback Component
 *
 * Renders the default error UI with options to retry or go home.
 */
export function DefaultErrorFallback({
  error,
  errorInfo,
  resetError,
  eventId,
  showDetails = false,
}: ErrorFallbackProps) {
  const [showErrorDetails, setShowErrorDetails] = useState(false);

  const copyErrorToClipboard = async () => {
    const errorText = `
Error: ${error.message}
Event ID: ${eventId ?? "N/A"}
Stack: ${error.stack}
Component Stack: ${errorInfo?.componentStack}
Timestamp: ${new Date().toISOString()}
    `.trim();

    try {
      await navigator.clipboard.writeText(errorText);
      // You could add a toast notification here
    } catch (err) {
      logger.warn("Failed to copy error to clipboard", { error: err });
    }
  };

  return (
    <div className="flex min-h-[400px] w-full items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-destructive" />
            <CardTitle className="text-destructive">
              Something went wrong
            </CardTitle>
          </div>
          <CardDescription>
            An unexpected error occurred. Our team has been notified and will
            investigate the issue.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {eventId && (
            <div className="rounded-md bg-muted p-3">
              <p className="text-sm font-medium">Error ID: {eventId}</p>
              <p className="text-xs text-muted-foreground">
                Please include this ID when reporting the issue
              </p>
            </div>
          )}

          <div className="flex flex-col gap-3 sm:flex-row">
            <Button onClick={resetError} className="flex-1">
              <RefreshCw className="mr-2 h-4 w-4" />
              Try Again
            </Button>
            <Button
              variant="outline"
              onClick={() => (window.location.href = "/")}
              className="flex-1"
            >
              <Home className="mr-2 h-4 w-4" />
              Go Home
            </Button>
          </div>

          {showDetails && (
            <div className="space-y-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowErrorDetails(!showErrorDetails)}
                className="h-auto p-0 text-sm text-muted-foreground"
              >
                <Bug className="mr-1 h-3 w-3" />
                {showErrorDetails ? "Hide" : "Show"} Error Details
              </Button>

              {showErrorDetails && (
                <div className="space-y-2">
                  <div className="rounded-md bg-muted p-3">
                    <p className="text-sm font-medium">Error Message:</p>
                    <code className="text-xs">{error.message}</code>
                  </div>

                  {error.stack && (
                    <div className="rounded-md bg-muted p-3">
                      <p className="text-sm font-medium">Stack Trace:</p>
                      <pre className="mt-1 max-h-32 overflow-auto text-xs">
                        {error.stack}
                      </pre>
                    </div>
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyErrorToClipboard}
                    className="w-full"
                  >
                    Copy Error Details
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
